import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import './index.css';
import { fr as localeDateFnsFr } from 'date-fns/locale/fr';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';
import { ModalProvider } from './components/modals';
import { SheetProvider } from './components/sheets';
import { TooltipProvider } from './components/ui/tooltip';
import { createi18n } from './i18n';
import { createQueryClient } from './query-client';
import { createRouter } from './router';
import { SentryService } from './sentry';
import envService from './utils/env.service';

const isProduction = envService.env.envName === 'production';

async function enableMocking() {
  const { worker } = await import('./mocks/browser');
  return worker.start();
}

const i18n = createi18n();
const router = createRouter();
const queryClient = createQueryClient({ router, t: i18n.t });

async function bootstrap() {
  if (!isProduction && envService.env.api.mock) {
    return enableMocking();
  }
  return Promise.resolve(true);
}

setDefaultOptions({ locale: localeDateFnsFr });
SentryService.init({
  envName: envService.env.envName,
  enabled: envService.env.sentry.enabled,
  apiUrl: envService.env.api.url,
});

bootstrap().then(() => {
  const rootElement = document.getElementById('root')!;
  if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);

    root.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <TooltipProvider>
              <RouterProvider router={router} />
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-right"
              />
              <Toaster richColors />
              <ModalProvider />
              <SheetProvider />
            </TooltipProvider>
          </I18nextProvider>
        </QueryClientProvider>
      </StrictMode>,
    );
  }
});
